/**
 * RoleGuard component for protecting routes based on user roles.
 *
 * This component checks if the authenticated user has the required role
 * to access a specific route. Role is fetched from database (single source of truth).
 * If not authorized, it redirects to the chat page with an error message.
 *
 * Feature: 001-user-role-management (User Story 1 - T037)
 * Security: Role is fetched from backend database, not from Clerk metadata
 */
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface RoleGuardProps {
  children: ReactNode;
  requiredRole: 'admin' | 'user';
  redirectTo?: string;
}

/**
 * RoleGuard component that protects routes based on user roles from database.
 *
 * @param children - Components to render if user is authorized
 * @param requiredRole - The role required to access this route ('admin' or 'user')
 * @param redirectTo - Optional path to redirect to if unauthorized (default: '/chat')
 */
export function RoleGuard({
  children,
  requiredRole,
  redirectTo = '/chat'
}: RoleGuardProps) {
  const { user, role, isAuthenticated, isLoading, clerkUser } = useAuth();
  const location = useLocation();

  // Wait for authentication and role to load
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-surface mx-auto"></div>
          <p className="mt-4 text-brand-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // User must be authenticated (has user object with role from database)
  if (!isAuthenticated || !user) {
    // If there's no Clerk user, the SignedOut component in App.tsx will handle the redirect
    // Just show loading state here - Clerk's RedirectToSignIn will take over
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-surface mx-auto"></div>
          <p className="mt-4 text-brand-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // Get user role from database (via AuthContext)
  const userRole = role;

  // Check if user has the required role
  // Admins can access everything, or user must have the specific required role
  const isAuthorized = userRole === requiredRole || userRole === 'admin';

  if (!isAuthorized) {
    // Store the error message and attempted path in session storage
    sessionStorage.setItem('auth_error', `You don't have permission to access ${location.pathname}`);
    sessionStorage.setItem('auth_attempted_path', location.pathname);

    // Redirect to the specified path (default: /chat)
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // User is authorized, render the protected content
  return <>{children}</>;
}

/**
 * AdminGuard - Convenience wrapper for admin-only routes
 */
export function AdminGuard({ children }: { children: ReactNode }) {
  return (
    <RoleGuard requiredRole="admin" redirectTo="/chat">
      {children}
    </RoleGuard>
  );
}

/**
 * UserGuard - Convenience wrapper for user-accessible routes
 */
export function UserGuard({ children }: { children: ReactNode }) {
  return (
    <RoleGuard requiredRole="user" redirectTo="/chat">
      {children}
    </RoleGuard>
  );
}
